import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { MessageCircle, Calendar, User, ThumbsUp, ArrowLeft } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import toast from 'react-hot-toast';

interface Post {
  id: string;
  titulo: string;
  conteudo: string;
  autor_nome: string;
  autor_setor: string;
  imagem_url?: string;
  created_at: string;
  likes_count: number;
  comments_count: number;
}

interface Comment {
  id: string;
  texto: string;
  autor_nome: string;
  created_at: string;
}

export const PublicPost: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const postId = searchParams.get('post');
  const [post, setPost] = useState<Post | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (postId) {
      loadPost();
    } else {
      setNotFound(true);
      setLoading(false);
    }
  }, [postId]);

  const loadPost = async () => {
    try {
      setLoading(true);

      const { data: postData, error: postError } = await supabase
        .from('mural_posts')
        .select('*')
        .eq('id', postId)
        .eq('publicado', true)
        .single();

      if (postError || !postData) {
        setNotFound(true);
        return;
      }

      const { count: likesCount } = await supabase
        .from('mural_likes')
        .select('*', { count: 'exact', head: true })
        .eq('post_id', postId);

      const { count: commentsCount } = await supabase
        .from('mural_comentarios')
        .select('*', { count: 'exact', head: true })
        .eq('post_id', postId);

      setPost({
        ...postData,
        likes_count: likesCount || 0,
        comments_count: commentsCount || 0,
      });

      const { data: commentsData, error: commentsError } = await supabase
        .from('mural_comentarios')
        .select('*')
        .eq('post_id', postId)
        .order('created_at', { ascending: true });

      if (!commentsError) {
        setComments(commentsData || []);
      }
    } catch (error) {
      console.error('Error loading post:', error);
      toast.error('Erro ao carregar publicação');
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  const renderTextWithLinks = (text: string) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);

    return parts.map((part, index) => {
      if (part.match(urlRegex)) {
        return (
          <a
            key={index}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:text-primary-dark underline break-all"
            onClick={(e) => e.stopPropagation()}
          >
            {part}
          </a>
        );
      }
      return <span key={index}>{part}</span>;
    });
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), "d 'de' MMMM 'de' yyyy 'às' HH:mm", { locale: ptBR });
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-surface dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-secondary font-medium">Carregando publicação...</p>
        </div>
      </div>
    );
  }

  if (notFound || !post) {
    return (
      <div className="min-h-screen bg-surface dark:bg-gray-900 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-outline-variant p-8 max-w-md w-full text-center">
          <div className="w-20 h-20 bg-error/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <MessageCircle className="w-10 h-10 text-error" />
          </div>
          <h1 className="text-2xl font-bold text-on-surface mb-2">Publicação não encontrada</h1>
          <p className="text-secondary mb-6">
            A publicação que você está procurando não existe ou foi removida.
          </p>
          <button
            onClick={() => navigate('/login')}
            className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors font-medium"
          >
            Fazer Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface dark:bg-gray-900 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="mb-6">
          <button
            onClick={() => navigate('/login')}
            className="flex items-center space-x-2 text-primary hover:text-primary-dark transition-colors font-medium"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Fazer Login na Intranet</span>
          </button>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-outline-variant overflow-hidden">
          <div className="bg-white dark:bg-gray-800 px-6 py-6 border-b border-outline-variant">
            <div className="flex items-center justify-center">
              <img
                src="/gcf_logo_01.png"
                alt="Grupo Cropfield"
                className="h-16 w-auto"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            </div>
          </div>

          <div className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                  <User className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-on-surface">{post.autor_nome}</h3>
                  <p className="text-sm text-secondary">{post.autor_setor}</p>
                  <div className="flex items-center space-x-1 text-xs text-secondary mt-1">
                    <Calendar className="w-3 h-3" />
                    <span>{formatDate(post.created_at)}</span>
                  </div>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-on-surface mb-3">{post.titulo}</h2>
            <p className="text-on-surface mb-4 whitespace-pre-wrap leading-relaxed">
              {renderTextWithLinks(post.conteudo)}
            </p>

            {post.imagem_url && (
              <div className="mb-4 flex justify-center">
                <img
                  src={post.imagem_url}
                  alt="Imagem da publicação"
                  className="rounded-lg max-w-full h-auto"
                />
              </div>
            )}

            <div className="flex items-center space-x-6 pt-4 border-t border-outline-variant">
              <div className="flex items-center space-x-2 text-secondary">
                <ThumbsUp className="w-5 h-5" />
                <span className="text-sm font-medium">{post.likes_count} curtidas</span>
              </div>
              <div className="flex items-center space-x-2 text-secondary">
                <MessageCircle className="w-5 h-5" />
                <span className="text-sm font-medium">{post.comments_count} comentários</span>
              </div>
            </div>

            {comments.length > 0 && (
              <div className="mt-6 space-y-4 pt-6 border-t border-outline-variant">
                <h3 className="font-semibold text-on-surface mb-4">Comentários</h3>
                {comments.map(comment => (
                  <div key={comment.id} className="flex space-x-3 bg-surface-container-low rounded-lg p-4">
                    <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                      <User className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="font-medium text-on-surface">{comment.autor_nome}</span>
                        <span className="text-xs text-secondary">{formatDate(comment.created_at)}</span>
                      </div>
                      <p className="text-on-surface text-sm leading-relaxed">
                        {renderTextWithLinks(comment.texto)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-8 p-4 bg-primary/5 rounded-lg border border-outline-variant">
              <p className="text-sm text-on-surface text-center">
                <strong>Quer interagir com esta publicação?</strong>
                <br />
                Faça login na intranet para curtir e comentar.
              </p>
              <button
                onClick={() => navigate('/login')}
                className="w-full mt-3 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors font-medium"
              >
                Fazer Login
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
